/**
 * Sysadmin Models — Brand detail (Wave 3 / M6c).
 *
 * Shows full detail for a single brand: engine pool, function-chain
 * composition, tenant assignment summary, factory_default flag,
 * sortOrder, retire status. Sysadmin can edit any field via inline
 * controls (Phase C+ work).
 *
 * For Phase B this surface is read-only — it loads useArgusModelDetail
 * and renders the engine + chain tables. Phase C adds the inline
 * edit controls + the "add engine" / "edit chain" affordances.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  Sparkles,
  CircleDot,
  AlertTriangle,
  Server,
  Sparkle,
  PackageOpen,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button, buttonVariants } from '@/components/ui/button';
import { useArgusModelDetail } from '@/lib/use-argus-models';
import { WrapperChainEditor } from '@/components/sysadmin/wrapper-chain-editor';
import { EngineEditDialog, type EngineLike } from '@/components/sysadmin/engine-edit-dialog';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/models/$id')({
  component: ModelDetailPage,
});

function ModelDetailPage() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = useArgusModelDetail(id);
  const [editEngine, setEditEngine] = useState<EngineLike | null>(null);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/sysadmin/models"
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to catalog
        </Link>
      </div>

      {error ? (
        <Card>
          <CardContent className="py-8 text-center text-destructive text-sm">
            Failed to load brand detail: {error.message}
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm animate-pulse">
            Loading brand detail…
          </CardContent>
        </Card>
      ) : (
        <>
          {/*
            * R5 P1-1: deferred banner. When the brand is retired with a
            * known deferral reason (Gemma 4 E2B segfault on argus_lite,
            * FLUX runtime unavailable on argus_draw), surface the exact
            * cause + recovery path on top of the detail page. Generic
            * retired brands without a known reason get no banner.
            */}
          {!data.isActive && (data.brandId === 'argus_lite' || data.brandId === 'argus_draw') && (
            <Alert
              variant="default"
              className="border-amber-300 bg-amber-50 text-amber-900"
            >
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertTitle className="text-amber-900 font-semibold">
                DEFERRED —{' '}
                {data.brandId === 'argus_lite'
                  ? 'Gemma 4 not deployed'
                  : 'image-gen runtime not deployed'}
              </AlertTitle>
              <AlertDescription className="text-amber-800 text-sm">
                {data.brandId === 'argus_lite' ? (
                  <>
                    The Gemma 4 E2B engine that backs ArgusLite segfaults at
                    <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">srv load_model: initializing slots</code>
                    on the Strix Halo gfx1100 b8987 build. The engine is
                    correctly marked <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">is_active=false</code> and
                    every assigned tenant has been unassigned automatically.
                    {' '}<strong>Recovery:</strong> add an alternate engine (e.g.
                    gpt-oss-20b shared with ArgusFast) via the Engine pool
                    table below, then re-enable the brand + assignments.
                  </>
                ) : (
                  <>
                    The FLUX-1 Schnell image-gen runtime that backs ArgusDraw
                    reports <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">visibleAtGateway=false</code> —
                    the runtime container is not deployed in this stack. The
                    brand has been disabled and assignments cleared so the
                    chat picker does not promise a backend the gateway cannot
                    reach. <strong>Recovery:</strong> deploy the
                    <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">image-gen-runtime</code> container, verify the
                    gateway sees <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">flux-schnell</code> as an
                    available model, then flip is_active=TRUE.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {data.displayName}
                    {data.factoryDefault && (
                      <Badge variant="secondary" className="text-[10px] gap-0.5">
                        <Sparkles className="h-3 w-3" />
                        factory
                      </Badge>
                    )}
                    {data.purpose === 'infra' ? (
                      <Badge
                        variant="outline"
                        className="text-blue-700 border-blue-300 gap-1 text-[10px]"
                        title="System infrastructure brand — not selectable by tenants."
                      >
                        <Server className="h-3 w-3" />
                        Infrastructure
                      </Badge>
                    ) : data.isActive ? (
                      data.engineCount === 0 ? (
                        <Badge
                          variant="outline"
                          className="text-amber-700 border-amber-300 gap-1 text-[10px]"
                          title="No active engine — dispatch will fail."
                        >
                          <AlertTriangle className="h-3 w-3" />
                          no active engine
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-300 gap-1 text-[10px]">
                          <CircleDot className="h-3 w-3" />
                          active
                        </Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-[10px]">retired</Badge>
                    )}
                  </CardTitle>
                  <div className="text-sm text-muted-foreground font-mono mt-0.5">
                    {data.brandId}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground space-y-0.5">
                  <div>Modality: <span className="font-medium text-foreground">{data.modality}</span></div>
                  <div>Author: <span className="font-medium text-foreground">{data.author}</span></div>
                  <div>Sort: <span className="font-mono">{data.sortOrder}</span></div>
                </div>
              </div>
              <CardDescription className="mt-2">{data.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <Stat label="Engines" value={data.engineCount} />
                <Stat label="Tenants" value={`${data.enabledAssignmentCount} / ${data.assignmentCount}`} />
                <Stat label="Chain entries" value={data.chains.length} />
              </div>
            </CardContent>
          </Card>

          {/*
            * R5 P1-3 cross-rail navigation. Deep-links from the brand
            * detail to existing sysadmin surfaces that operate on this
            * brand's underlying engines. Source-of-truth pages live at
            * /sysadmin/fine-tuning/adapters and /sysadmin/platform/airgap
            * respectively; these buttons add cross-rail visibility so
            * sysadmin doesn't have to navigate the IA tree manually.
            *
            * Per the audit prompt: do not move the source pages, just
            * add nav hooks here.
            */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Engine lifecycle</CardTitle>
              <CardDescription>
                Brand-scoped shortcuts to the fine-tuning + air-gap import
                surfaces. Each opens the relevant sysadmin rail with this
                brand pre-selected where supported.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/sysadmin/fine-tuning/adapters"
                  search={{ brandId: data.brandId } as never}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'gap-1.5',
                  )}
                  title="Manage LoRA adapters and the blue/green deploy state machine for this brand. Source page lives at /sysadmin/fine-tuning/adapters."
                >
                  <Sparkle className="h-3.5 w-3.5" />
                  Blue/green LoRA deploy
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </Link>
                <Link
                  to="/sysadmin/platform/airgap"
                  search={{ brandId: data.brandId } as never}
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'gap-1.5',
                  )}
                  title="Air-gap operations: network-egress allowlist + (planned M6c+) offline model-weight import. Source page lives at /sysadmin/platform/airgap."
                >
                  <PackageOpen className="h-3.5 w-3.5" />
                  Import new version
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </Link>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Source pages own state; these links provide cross-rail
                visibility without duplicating the workflow.
              </p>
            </CardContent>
          </Card>

          {/* Engines */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Engine pool</CardTitle>
              <CardDescription>
                Engines available for this brand. Sysadmin assigns one per
                tenant via the Assignments matrix.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Base LLM</TableHead>
                    <TableHead>Provenance</TableHead>
                    <TableHead>Eligibility</TableHead>
                    <TableHead className="text-right">Ctx</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right w-14">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.engines.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <span className="font-medium">{e.roleLabel}</span>
                        {e.isDefault && (
                          <Badge variant="secondary" className="ml-2 text-[10px]">default</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.baseLlmModelKey}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {e.baseLlmProvenance}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {(e.provenanceEligibility ?? []).join(', ')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {e.defaultCtxSize.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {e.isActive ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground text-[10px]">retired</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Edit engine config"
                          onClick={() => setEditEngine(e as unknown as EngineLike)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Wrapper chain — editable (add / edit / remove). */}
          <WrapperChainEditor brandId={id} chains={data.chains} />
        </>
      )}

      <EngineEditDialog
        brandId={id}
        engine={editEngine}
        open={!!editEngine}
        onOpenChange={(o) => { if (!o) setEditEngine(null); }}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
